import React, { useState, useEffect, useRef } from 'react';
import OpenSeadragon from 'openseadragon';

interface MeasurementToolsProps {
  viewer: OpenSeadragon.Viewer | null;
  pixelsPerMicron?: number; // Calibration value
  onCalibrationChange?: (pixelsPerMicron: number) => void;
}

type MeasurementMode = 'none' | 'linear' | 'area' | 'angle' | 'calibrate';
type AreaShape = 'rectangle' | 'circle' | 'polygon';

interface Measurement {
  id: string;
  type: MeasurementMode;
  points: OpenSeadragon.Point[];
  value: number;
  unit: string;
  color: string;
  label?: string;
}

export function MeasurementTools({ 
  viewer, 
  pixelsPerMicron = 0.25, // Default: 4 pixels per micron
  onCalibrationChange 
}: MeasurementToolsProps) {
  const [mode, setMode] = useState<MeasurementMode>('none');
  const [areaShape, setAreaShape] = useState<AreaShape>('rectangle');
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [currentPoints, setCurrentPoints] = useState<OpenSeadragon.Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showScaleBar, setShowScaleBar] = useState(true);
  const [calibrationValue, setCalibrationValue] = useState(pixelsPerMicron);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);

  // Measurement colors
  const colors = {
    linear: '#00ff00',
    area: '#ffff00',
    angle: '#ff00ff',
    active: '#ff0000',
    grid: 'rgba(255, 255, 255, 0.2)'
  };

  // Initialize canvas overlay
  useEffect(() => {
    if (!viewer) return;

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.left = '0';
    canvas.style.top = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '10';
    
    const viewerElement = viewer.element;
    if (viewerElement) {
      viewerElement.appendChild(canvas);
      overlayRef.current = canvas;
      contextRef.current = canvas.getContext('2d');
      
      // Update canvas size
      const updateCanvasSize = () => {
        if (canvas && viewerElement) {
          canvas.width = viewerElement.clientWidth;
          canvas.height = viewerElement.clientHeight;
          redrawMeasurements();
        }
      };
      
      updateCanvasSize();
      viewer.addHandler('resize', updateCanvasSize);
      viewer.addHandler('update-viewport', redrawMeasurements);
      
      return () => {
        viewer.removeHandler('resize', updateCanvasSize);
        viewer.removeHandler('update-viewport', redrawMeasurements);
        canvas.remove();
      };
    }
  }, [viewer]);

  // Handle mouse events for measurements
  useEffect(() => {
    if (!viewer || mode === 'none') return;

    const handleClick = (event: OpenSeadragon.MouseTrackerEvent) => {
      if (!event.position) return;
      
      const viewportPoint = viewer.viewport.pointFromPixel(event.position);
      
      switch (mode) {
        case 'linear':
          handleLinearMeasurement(viewportPoint);
          break;
        case 'area':
          handleAreaMeasurement(viewportPoint);
          break;
        case 'angle':
          handleAngleMeasurement(viewportPoint);
          break;
        case 'calibrate':
          handleCalibration(viewportPoint);
          break;
      }
    };

    viewer.addHandler('canvas-click', handleClick);
    
    return () => {
      viewer.removeHandler('canvas-click', handleClick);
    };
  }, [viewer, mode, currentPoints]);

  // Linear measurement (2 points)
  const handleLinearMeasurement = (point: OpenSeadragon.Point) => {
    const newPoints = [...currentPoints, point];
    
    if (newPoints.length === 2) {
      const distance = calculateDistance(newPoints[0], newPoints[1]);
      const measurement: Measurement = {
        id: Date.now().toString(),
        type: 'linear',
        points: newPoints,
        value: distance / calibrationValue,
        unit: 'μm',
        color: colors.linear,
        label: `${(distance / calibrationValue).toFixed(1)} μm`
      };
      
      setMeasurements([...measurements, measurement]);
      setCurrentPoints([]);
    } else {
      setCurrentPoints(newPoints);
    }
  };

  // Area measurement (depends on shape)
  const handleAreaMeasurement = (point: OpenSeadragon.Point) => {
    const newPoints = [...currentPoints, point];
    
    if (areaShape === 'rectangle' && newPoints.length === 2) {
      const area = calculateRectangleArea(newPoints[0], newPoints[1]);
      const measurement: Measurement = {
        id: Date.now().toString(),
        type: 'area',
        points: newPoints,
        value: area / (calibrationValue * calibrationValue),
        unit: 'μm²',
        color: colors.area,
        label: `${(area / (calibrationValue * calibrationValue)).toFixed(1)} μm²`
      };
      
      setMeasurements([...measurements, measurement]);
      setCurrentPoints([]);
    } else if (areaShape === 'circle' && newPoints.length === 2) {
      const radius = calculateDistance(newPoints[0], newPoints[1]);
      const area = Math.PI * radius * radius;
      const measurement: Measurement = {
        id: Date.now().toString(),
        type: 'area',
        points: newPoints,
        value: area / (calibrationValue * calibrationValue),
        unit: 'μm²',
        color: colors.area,
        label: `${(area / (calibrationValue * calibrationValue)).toFixed(1)} μm²`
      };
      
      setMeasurements([...measurements, measurement]);
      setCurrentPoints([]);
    } else if (areaShape === 'polygon' && newPoints.length >= 3) {
      // Close polygon on right click or double click
      // For now, we'll close after 5 points
      if (newPoints.length === 5) {
        const area = calculatePolygonArea(newPoints);
        const measurement: Measurement = {
          id: Date.now().toString(),
          type: 'area',
          points: newPoints,
          value: area / (calibrationValue * calibrationValue),
          unit: 'μm²',
          color: colors.area,
          label: `${(area / (calibrationValue * calibrationValue)).toFixed(1)} μm²`
        };
        
        setMeasurements([...measurements, measurement]);
        setCurrentPoints([]);
      } else {
        setCurrentPoints(newPoints);
      }
    } else {
      setCurrentPoints(newPoints);
    }
  };

  // Angle measurement (3 points)
  const handleAngleMeasurement = (point: OpenSeadragon.Point) => {
    const newPoints = [...currentPoints, point];
    
    if (newPoints.length === 3) {
      const angle = calculateAngle(newPoints[0], newPoints[1], newPoints[2]);
      const measurement: Measurement = {
        id: Date.now().toString(),
        type: 'angle',
        points: newPoints,
        value: angle,
        unit: '°',
        color: colors.angle,
        label: `${angle.toFixed(1)}°`
      };
      
      setMeasurements([...measurements, measurement]);
      setCurrentPoints([]);
    } else {
      setCurrentPoints(newPoints);
    }
  };

  // Calibration (2 points for known distance)
  const handleCalibration = (point: OpenSeadragon.Point) => {
    const newPoints = [...currentPoints, point];
    
    if (newPoints.length === 2) {
      const pixelDistance = calculateDistance(newPoints[0], newPoints[1]);
      // Prompt user for actual distance in microns
      const actualDistance = prompt('Εισάγετε την πραγματική απόσταση σε μικρόμετρα (μm):');
      
      if (actualDistance) {
        const newCalibration = pixelDistance / parseFloat(actualDistance);
        setCalibrationValue(newCalibration);
        onCalibrationChange?.(newCalibration);
        alert(`Βαθμονόμηση ενημερώθηκε: ${newCalibration.toFixed(4)} pixels/μm`);
      }
      
      setCurrentPoints([]);
      setMode('none');
    } else {
      setCurrentPoints(newPoints);
    }
  };

  // Calculate distance between two points
  const calculateDistance = (p1: OpenSeadragon.Point, p2: OpenSeadragon.Point): number => {
    if (!viewer) return 0;
    const pixelP1 = viewer.viewport.pixelFromPoint(p1);
    const pixelP2 = viewer.viewport.pixelFromPoint(p2);
    return Math.sqrt(Math.pow(pixelP2.x - pixelP1.x, 2) + Math.pow(pixelP2.y - pixelP1.y, 2));
  };

  // Calculate rectangle area
  const calculateRectangleArea = (p1: OpenSeadragon.Point, p2: OpenSeadragon.Point): number => {
    if (!viewer) return 0;
    const pixelP1 = viewer.viewport.pixelFromPoint(p1);
    const pixelP2 = viewer.viewport.pixelFromPoint(p2);
    return Math.abs((pixelP2.x - pixelP1.x) * (pixelP2.y - pixelP1.y));
  };

  // Calculate polygon area (Shoelace formula)
  const calculatePolygonArea = (points: OpenSeadragon.Point[]): number => {
    if (!viewer || points.length < 3) return 0;
    
    const pixelPoints = points.map(p => viewer.viewport.pixelFromPoint(p));
    let area = 0;
    
    for (let i = 0; i < pixelPoints.length; i++) {
      const j = (i + 1) % pixelPoints.length;
      area += pixelPoints[i].x * pixelPoints[j].y;
      area -= pixelPoints[j].x * pixelPoints[i].y;
    }
    
    return Math.abs(area / 2);
  };

  // Calculate angle between three points
  const calculateAngle = (p1: OpenSeadragon.Point, p2: OpenSeadragon.Point, p3: OpenSeadragon.Point): number => {
    if (!viewer) return 0;
    
    const pixelP1 = viewer.viewport.pixelFromPoint(p1);
    const pixelP2 = viewer.viewport.pixelFromPoint(p2);
    const pixelP3 = viewer.viewport.pixelFromPoint(p3);
    
    const v1 = { x: pixelP1.x - pixelP2.x, y: pixelP1.y - pixelP2.y };
    const v2 = { x: pixelP3.x - pixelP2.x, y: pixelP3.y - pixelP2.y };
    
    const angle = Math.atan2(v2.y, v2.x) - Math.atan2(v1.y, v1.x);
    const degrees = angle * 180 / Math.PI;
    
    return degrees < 0 ? degrees + 360 : degrees;
  };

  // Redraw all measurements
  const redrawMeasurements = () => {
    if (!contextRef.current || !viewer || !overlayRef.current) return;
    
    const ctx = contextRef.current;
    ctx.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    
    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx);
    }
    
    // Draw scale bar if enabled
    if (showScaleBar) {
      drawScaleBar(ctx);
    }
    
    // Draw measurements
    measurements.forEach(measurement => {
      drawMeasurement(ctx, measurement);
    });
    
    // Draw current points
    if (currentPoints.length > 0) {
      drawCurrentPoints(ctx);
    }
  };

  // Draw measurement on canvas
  const drawMeasurement = (ctx: CanvasRenderingContext2D, measurement: Measurement) => {
    if (!viewer) return;
    
    ctx.strokeStyle = measurement.color;
    ctx.fillStyle = measurement.color;
    ctx.lineWidth = 2;
    ctx.font = '14px Arial';
    
    const pixelPoints = measurement.points.map(p => viewer.viewport.pixelFromPoint(p));
    
    switch (measurement.type) {
      case 'linear':
        // Draw line
        ctx.beginPath();
        ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
        ctx.lineTo(pixelPoints[1].x, pixelPoints[1].y);
        ctx.stroke();
        
        // Draw endpoints
        pixelPoints.forEach(p => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
          ctx.fill();
        });
        
        // Draw label
        const midX = (pixelPoints[0].x + pixelPoints[1].x) / 2;
        const midY = (pixelPoints[0].y + pixelPoints[1].y) / 2;
        ctx.fillText(measurement.label || '', midX + 5, midY - 5);
        break;
        
      case 'area':
        if (pixelPoints.length === 2) {
          // Rectangle or circle
          const width = pixelPoints[1].x - pixelPoints[0].x;
          const height = pixelPoints[1].y - pixelPoints[0].y;
          
          ctx.beginPath();
          ctx.rect(pixelPoints[0].x, pixelPoints[0].y, width, height);
          ctx.stroke();
        } else {
          // Polygon
          ctx.beginPath();
          ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
          for (let i = 1; i < pixelPoints.length; i++) {
            ctx.lineTo(pixelPoints[i].x, pixelPoints[i].y);
          }
          ctx.closePath();
          ctx.stroke();
        }
        
        // Draw label
        const centerX = pixelPoints.reduce((sum, p) => sum + p.x, 0) / pixelPoints.length;
        const centerY = pixelPoints.reduce((sum, p) => sum + p.y, 0) / pixelPoints.length;
        ctx.fillText(measurement.label || '', centerX - 20, centerY);
        break;
        
      case 'angle':
        // Draw angle lines
        ctx.beginPath();
        ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
        ctx.lineTo(pixelPoints[1].x, pixelPoints[1].y);
        ctx.lineTo(pixelPoints[2].x, pixelPoints[2].y);
        ctx.stroke();
        
        // Draw angle arc
        const radius = 30;
        const v1 = Math.atan2(pixelPoints[0].y - pixelPoints[1].y, pixelPoints[0].x - pixelPoints[1].x);
        const v2 = Math.atan2(pixelPoints[2].y - pixelPoints[1].y, pixelPoints[2].x - pixelPoints[1].x);
        
        ctx.beginPath();
        ctx.arc(pixelPoints[1].x, pixelPoints[1].y, radius, v1, v2);
        ctx.stroke();
        
        // Draw label
        ctx.fillText(measurement.label || '', pixelPoints[1].x + 35, pixelPoints[1].y - 5);
        break;
    }
  };

  // Draw current points being measured
  const drawCurrentPoints = (ctx: CanvasRenderingContext2D) => {
    if (!viewer) return;
    
    ctx.strokeStyle = colors.active;
    ctx.fillStyle = colors.active;
    ctx.lineWidth = 2;
    
    const pixelPoints = currentPoints.map(p => viewer.viewport.pixelFromPoint(p));
    
    // Draw points
    pixelPoints.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
      ctx.fill();
    });
    
    // Draw connecting lines
    if (pixelPoints.length > 1) {
      ctx.beginPath();
      ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y);
      for (let i = 1; i < pixelPoints.length; i++) {
        ctx.lineTo(pixelPoints[i].x, pixelPoints[i].y);
      }
      ctx.stroke();
    }
  };

  // Draw grid overlay
  const drawGrid = (ctx: CanvasRenderingContext2D) => {
    if (!viewer || !overlayRef.current) return;
    
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 1;
    
    const gridSize = 50; // pixels
    const width = overlayRef.current.width;
    const height = overlayRef.current.height;
    
    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // Horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // Draw scale bar
  const drawScaleBar = (ctx: CanvasRenderingContext2D) => {
    if (!viewer || !overlayRef.current) return;
    
    const zoom = viewer.viewport.getZoom();
    const scaleBarMicrons = getScaleBarLength(zoom);
    const scaleBarPixels = scaleBarMicrons * calibrationValue;
    
    const x = overlayRef.current.width - scaleBarPixels - 50;
    const y = overlayRef.current.height - 40;
    
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.font = 'bold 14px Arial';
    
    // Draw bar
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + scaleBarPixels, y);
    ctx.stroke();
    
    // Draw end caps
    ctx.beginPath();
    ctx.moveTo(x, y - 5);
    ctx.lineTo(x, y + 5);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(x + scaleBarPixels, y - 5);
    ctx.lineTo(x + scaleBarPixels, y + 5);
    ctx.stroke();
    
    // Draw label
    const label = scaleBarMicrons >= 1000 ? `${scaleBarMicrons / 1000} mm` : `${scaleBarMicrons} μm`;
    ctx.fillText(label, x + scaleBarPixels / 2 - 20, y - 10);
  };

  // Get appropriate scale bar length based on zoom
  const getScaleBarLength = (zoom: number): number => {
    const baseLength = 100; // microns at zoom level 1
    const adjustedLength = baseLength / zoom;
    
    // Round to nice numbers
    if (adjustedLength <= 10) return 10;
    if (adjustedLength <= 20) return 20;
    if (adjustedLength <= 50) return 50;
    if (adjustedLength <= 100) return 100;
    if (adjustedLength <= 200) return 200;
    if (adjustedLength <= 500) return 500;
    if (adjustedLength <= 1000) return 1000;
    if (adjustedLength <= 2000) return 2000;
    return 5000;
  };

  // Clear all measurements
  const clearMeasurements = () => {
    setMeasurements([]);
    setCurrentPoints([]);
    redrawMeasurements();
  };

  // Delete specific measurement
  const deleteMeasurement = (id: string) => {
    setMeasurements(measurements.filter(m => m.id !== id));
  };

  // Update when measurements change
  useEffect(() => {
    redrawMeasurements();
  }, [measurements, currentPoints, showGrid, showScaleBar]);

  return (
    <div className="absolute top-20 right-4 z-20">
      <div className="bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 w-64">
        <h3 className="text-white text-sm font-semibold mb-3">Εργαλεία Μέτρησης</h3>
        
        {/* Tool selection */}
        <div className="space-y-2 mb-3">
          <button
            onClick={() => setMode(mode === 'linear' ? 'none' : 'linear')}
            className={`w-full px-3 py-2 text-xs font-medium rounded transition-colors ${
              mode === 'linear' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            📏 Γραμμική Μέτρηση
          </button>
          
          <div className="flex space-x-1">
            <button
              onClick={() => setMode(mode === 'area' ? 'none' : 'area')}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                mode === 'area' 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              ◻️ Επιφάνεια
            </button>
            
            {mode === 'area' && (
              <select
                value={areaShape}
                onChange={(e) => setAreaShape(e.target.value as AreaShape)}
                className="px-2 py-1 text-xs bg-gray-700 text-white rounded"
              >
                <option value="rectangle">Ορθογώνιο</option>
                <option value="circle">Κύκλος</option>
                <option value="polygon">Πολύγωνο</option>
              </select>
            )}
          </div>
          
          <button
            onClick={() => setMode(mode === 'angle' ? 'none' : 'angle')}
            className={`w-full px-3 py-2 text-xs font-medium rounded transition-colors ${
              mode === 'angle' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
          >
            📐 Γωνία
          </button>
        </div>
        
        {/* Display options */}
        <div className="border-t border-white/20 pt-2 mb-3">
          <div className="space-y-2">
            <label className="flex items-center justify-between text-xs text-gray-300">
              <span>Πλέγμα</span>
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                className="ml-2"
              />
            </label>
            
            <label className="flex items-center justify-between text-xs text-gray-300">
              <span>Κλίμακα</span>
              <input
                type="checkbox"
                checked={showScaleBar}
                onChange={(e) => setShowScaleBar(e.target.checked)}
                className="ml-2"
              />
            </label>
          </div>
        </div>
        
        {/* Calibration */}
        <div className="border-t border-white/20 pt-2 mb-3">
          <button
            onClick={() => setMode('calibrate')}
            className="w-full px-3 py-2 text-xs font-medium bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors"
          >
            🔧 Βαθμονόμηση
          </button>
          <p className="text-xs text-gray-400 mt-1">
            {calibrationValue.toFixed(3)} pixels/μm
          </p>
        </div>
        
        {/* Measurements list */}
        {measurements.length > 0 && (
          <div className="border-t border-white/20 pt-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-300">Μετρήσεις ({measurements.length})</span>
              <button
                onClick={clearMeasurements}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Καθαρισμός
              </button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {measurements.map((m) => (
                <div key={m.id} className="flex justify-between items-center text-xs">
                  <span className="text-gray-300">{m.label}</span>
                  <button
                    onClick={() => deleteMeasurement(m.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Mode indicator */}
        {mode !== 'none' && (
          <div className="mt-3 p-2 bg-blue-900 bg-opacity-50 rounded text-xs text-blue-200">
            {mode === 'linear' && 'Κλικ για 2 σημεία'}
            {mode === 'area' && areaShape === 'rectangle' && 'Κλικ για 2 γωνίες'}
            {mode === 'area' && areaShape === 'circle' && 'Κλικ κέντρο και ακτίνα'}
            {mode === 'area' && areaShape === 'polygon' && 'Κλικ για σημεία (max 5)'}
            {mode === 'angle' && 'Κλικ για 3 σημεία'}
            {mode === 'calibrate' && 'Μετρήστε γνωστή απόσταση'}
          </div>
        )}
      </div>
    </div>
  );
}